import { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Sparkles } from "@react-three/drei";
import * as THREE from "three";

const PLANET_COLORS = {
  Sun: "#F4C95D",
  Moon: "#E8E4D8",
  Mercury: "#C9A24B",
  Venus: "#E9B7C7",
  Mars: "#C1553B",
  Jupiter: "#D8A24B",
  Saturn: "#B7A98A",
};

const StarDome = ({ count = 1400 }) => {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 7 + Math.random() * 2;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);
  useFrame((_, d) => {
    if (ref.current) ref.current.rotation.y += d * 0.012;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.045} color="#EDE4CF" transparent opacity={0.9} sizeAttenuation depthWrite={false} />
    </points>
  );
};

// A real 3D moon: light direction is set by the lunation fraction so the
// visible terminator matches the actual phase.
const Moon = ({ fraction = 0.5 }) => {
  const grp = useRef();
  const phi = fraction * Math.PI * 2;
  const lightPos = [Math.sin(phi) * 5, 1, -Math.cos(phi) * 5];
  useFrame((_, d) => {
    if (grp.current) grp.current.rotation.y += d * 0.05;
  });
  return (
    <group>
      <directionalLight position={lightPos} intensity={2.6} color="#FFF6E2" />
      <group ref={grp}>
        <mesh>
          <sphereGeometry args={[1.35, 64, 64]} />
          <meshStandardMaterial color="#D8D2C4" roughness={1} metalness={0} emissive="#0a0a12" emissiveIntensity={0.35} />
        </mesh>
      </group>
      {/* soft halo */}
      <mesh>
        <sphereGeometry args={[1.55, 32, 32]} />
        <meshBasicMaterial color="#C5A059" transparent opacity={0.06} side={THREE.BackSide} />
      </mesh>
    </group>
  );
};

const EclipticRing = ({ planets }) => {
  const R = 3.4;
  return (
    <group rotation={[Math.PI / 2.4, 0, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[R, 0.006, 12, 160]} />
        <meshBasicMaterial color="#C5A059" transparent opacity={0.28} />
      </mesh>
      {Object.entries(planets || {}).map(([name, p]) => {
        const a = (p.ecliptic_lon * Math.PI) / 180;
        const x = R * Math.cos(a);
        const z = R * Math.sin(a);
        return (
          <mesh key={name} position={[x, 0, z]}>
            <sphereGeometry args={[name === "Sun" ? 0.14 : 0.08, 16, 16]} />
            <meshBasicMaterial color={PLANET_COLORS[name] || "#C5A059"} />
          </mesh>
        );
      })}
    </group>
  );
};

export const CelestialSky = ({ sky, className = "" }) => {
  const fraction = sky?.moon_phase?.cycle_fraction ?? 0.5;
  return (
    <div className={className} data-testid="celestial-sky">
      <Canvas camera={{ position: [0, 1.2, 6.5], fov: 50 }} dpr={[1, 2]}>
        <ambientLight intensity={0.08} />
        <StarDome />
        <Moon fraction={fraction} />
        {sky?.planets && <EclipticRing planets={sky.planets} />}
        <Sparkles count={60} scale={9} size={2} speed={0.2} color="#C5A059" opacity={0.5} />
      </Canvas>
    </div>
  );
};

export default CelestialSky;
